import { useCallback, useEffect, useRef } from 'react'
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { Popover } from 'components/Popover/Popover'
import { Close } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { numberWithCommas } from './utils'
import { findPlanCreditLimit } from 'components/Settings/Workspace/AutopilotPlans/utils'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { useRootStore } from 'stores/RootStore'
import Styles from './CreditsPopover.module.scss'
import { IconButton } from 'blaze-ui/IconButton/IconButton'

export interface CreditsBreakdownItem {
  label: string
  quantity: number
  creditsPerItem: number
  total: number
}

export interface CreditsPopoverProps {
  children: React.ReactNode
  totalSocialCount: number
  totalBlogCount: number
  totalEmailCount: number
  totalShortFormVideoCount: number
  monthlyCredits: number
  isGrowthPlan: boolean
  blogCountCreditsNeeded: number
  emailCountCreditsNeeded: number
  socialCountCreditsNeeded: number
  shortFormVideoCountCreditsNeeded: number
  stillImageCount?: number
  carouselCount?: number
  feedVideoCount?: number
  stillImageCreditsNeeded?: number
  carouselCreditsNeeded?: number
  feedVideoCreditsNeeded?: number
}

export interface CreditsPopoverContentProps extends Omit<CreditsPopoverProps, 'children'> {}

const ADDITIONAL_CREDITS_PRICE = '$10'
const ADDITIONAL_CREDITS_AMOUNT = 100

export const CreditsPopoverContent = ({
  totalSocialCount,
  totalBlogCount,
  totalEmailCount,
  totalShortFormVideoCount,
  monthlyCredits,
  isGrowthPlan,
  blogCountCreditsNeeded,
  emailCountCreditsNeeded,
  socialCountCreditsNeeded,
  shortFormVideoCountCreditsNeeded,
  stillImageCount = 0,
  carouselCount = 0,
  feedVideoCount = 0,
  stillImageCreditsNeeded = 0,
  carouselCreditsNeeded = 0,
  feedVideoCreditsNeeded = 0,
}: CreditsPopoverContentProps) => {
  const { tierUsageStore } = useRootStore()

  const hasPerTypeSocial = stillImageCount > 0 || carouselCount > 0 || feedVideoCount > 0

  const socialItems: CreditsBreakdownItem[] = hasPerTypeSocial
    ? [
        {
          label: 'Still images',
          quantity: stillImageCount,
          creditsPerItem: stillImageCount > 0 ? stillImageCreditsNeeded / stillImageCount : 0,
          total: stillImageCreditsNeeded,
        },
        {
          label: 'Carousels',
          quantity: carouselCount,
          creditsPerItem: carouselCount > 0 ? carouselCreditsNeeded / carouselCount : 0,
          total: carouselCreditsNeeded,
        },
        {
          label: 'Feed videos',
          quantity: feedVideoCount,
          creditsPerItem: feedVideoCount > 0 ? feedVideoCreditsNeeded / feedVideoCount : 0,
          total: feedVideoCreditsNeeded,
        },
      ].filter((item) => item.quantity > 0)
    : [
        {
          label: 'Social media posts',
          quantity: totalSocialCount,
          creditsPerItem: totalSocialCount > 0 ? socialCountCreditsNeeded / totalSocialCount : 0,
          total: socialCountCreditsNeeded,
        },
      ].filter((item) => item.quantity > 0)

  const breakdownItems: CreditsBreakdownItem[] = [
    ...socialItems,
    {
      label: 'Short-form videos',
      quantity: totalShortFormVideoCount,
      creditsPerItem: totalShortFormVideoCount > 0 ? shortFormVideoCountCreditsNeeded / totalShortFormVideoCount : 0,
      total: shortFormVideoCountCreditsNeeded,
    },
    {
      label: 'Blog posts',
      quantity: totalBlogCount,
      creditsPerItem: totalBlogCount > 0 ? blogCountCreditsNeeded / totalBlogCount : 0,
      total: blogCountCreditsNeeded,
    },
    {
      label: 'Emails',
      quantity: totalEmailCount,
      creditsPerItem: totalEmailCount > 0 ? emailCountCreditsNeeded / totalEmailCount : 0,
      total: emailCountCreditsNeeded,
    },
  ].filter((item) => item.quantity > 0)

  const weeklyCredits = breakdownItems.reduce((acc, item) => acc + item.total, 0)
  const planKey = isGrowthPlan ? PaymentPlanKey.BlazeAutopilotGrowth : PaymentPlanKey.BlazeAutopilotStarter
  const planCredits = findPlanCreditLimit(tierUsageStore.autopilotPlans, planKey)
  const additionalCreditsNeeded = Math.max(0, monthlyCredits - planCredits)

  return (
    <>
      <div className={Styles.content}>
        {breakdownItems.map((item) => (
          <div key={item.label} className={Styles.breakdownRow}>
            <Text variant="secondary" color="var(--dark-80)">
              {item.label}
            </Text>
            <div className={Styles.breakdownCalculation}>
              <Text variant="secondary" color="var(--dark-40)">
                {item.quantity} × {item.creditsPerItem} credits
              </Text>
              <div className={Styles.value}>
                <Text variant="secondary" color="var(--dark-80)">
                  {numberWithCommas(item.total)}
                </Text>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className={Styles.separator} />
      <div className={Styles.summary}>
        <div className={Styles.summaryRow}>
          <Text variant="secondary" color="var(--dark-40)">
            Total credits per week:
          </Text>
          <Text variant="secondary" color="var(--dark-80)">
            {numberWithCommas(weeklyCredits)}
          </Text>
        </div>
        <div className={Styles.summaryRow}>
          <Text variant="secondary" color="var(--dark-40)">
            Total credits per month:
          </Text>
          <Text variant="secondary" color="var(--dark-80)" fontWeight="500">
            {numberWithCommas(monthlyCredits)}
          </Text>
        </div>
      </div>
      <div className={Styles.separator} />
      <div className={Styles.summary}>
        <div className={Styles.summaryRow}>
          <Text variant="secondary" color="var(--dark-40)">
            {isGrowthPlan ? 'Growth' : 'Starter'} Plan credits:
          </Text>
          <Text variant="secondary" color="var(--dark-80)">
            {numberWithCommas(planCredits)}
          </Text>
        </div>
      </div>
      {additionalCreditsNeeded > 0 && (
        <>
          <div className={Styles.separator} />
          <div className={Styles.summary}>
            <div className={Styles.summaryRow}>
              <Text variant="secondary" color="var(--dark-40)">
                Additional credits needed:
              </Text>
              <Text variant="secondary" color="var(--dark-80)" fontWeight="500">
                {numberWithCommas(additionalCreditsNeeded)}
              </Text>
            </div>
            <div className={Styles.costNote}>
              <Text variant="label" color="var(--dark-40)">
                Additional credits cost {ADDITIONAL_CREDITS_PRICE} for {ADDITIONAL_CREDITS_AMOUNT}
              </Text>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export const CreditsPopover = ({ children, ...props }: CreditsPopoverProps) => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const overlayState = useOverlayTriggerState({ defaultOpen: false })
  const { overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, triggerRef)
  const { overlayProps: positionProps, updatePosition } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement: 'top',
    offset: 8,
    isOpen: overlayState.isOpen,
    boundaryElement: document.getElementById('workspace-dashboard') || document.body,
  })

  useEffect(() => updatePosition, [updatePosition, overlayState.isOpen])

  const positionStyle = positionProps.style ? { ...positionProps.style, zIndex: 1000 } : undefined

  const handleTriggerClick = useCallback(() => {
    overlayState.open()
  }, [overlayState])

  return (
    <div ref={triggerRef} className={Styles.trigger} onClick={handleTriggerClick}>
      {children}
      {overlayState.isOpen && (
        <OverlayContainer>
          <Popover
            {...overlayProps}
            {...positionProps}
            style={positionStyle}
            isOpen={overlayState.isOpen}
            onClose={overlayState.close}
            ref={overlayRef}
            isDismissable
            className={Styles.popover}
          >
            <div className={Styles.header}>
              <Heading level={5}>Credits breakdown</Heading>
              <IconButton
                icon={Close}
                aria-label="Close"
                variant="tertiary"
                size="sm"
                onPress={overlayState.close}
                className={Styles.closeButton}
              />
            </div>
            <CreditsPopoverContent {...props} />
          </Popover>
        </OverlayContainer>
      )}
    </div>
  )
}
